import React, { useState } from "react";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";

import { AppState } from "../../store/root.reducer";
import { ResetPassword } from "./store/auth.actions";
import Heading from "../../components/atoms/Heading";

import { Input, Form } from "../../components/molecules/Form";
import { validateEmail } from "../../utils/textUtils";
import styles from '../../components/modals/modal.module.css';

export const ForgotPassword = () => {
  const auth = useSelector((state: AppState) => state.auth);

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [email, setEmail] = useState<string>();
  
  // Track if email field has been modified to hide errors on typing
  const [emailTouched, setEmailTouched] = useState<boolean>(false);

  // Parse auth errors for field-specific display
  const getFieldError = (fieldName: string) => {
    if (!auth.authError) return null;
    
    // Hide errors if user has started typing in the field
    if (fieldName === 'email' && emailTouched) return null;
    
    // Check if auth.authError is already an object
    if (typeof auth.authError === 'object') {
      const fieldError = auth.authError[fieldName];
      
      // Ensure we return a string, not an object
      if (fieldError) {
        if (typeof fieldError === 'string') {
          return fieldError;
        } else if (typeof fieldError === 'object') {
          // If it's an object, try to extract message or convert to string
          return fieldError.message || fieldError.error || JSON.stringify(fieldError);
        }
        return String(fieldError);
      }
      return null;
    }
    
    // If it's a string, try to parse as JSON
    if (typeof auth.authError === 'string') {
      try {
        // Try to parse as JSON object for field-specific errors
        const errorObj = JSON.parse(auth.authError);
        const fieldError = errorObj[fieldName];
        
        // Ensure we return a string, not an object
        if (fieldError) {
          if (typeof fieldError === 'string') {
            return fieldError;
          } else if (typeof fieldError === 'object') {
            // If it's an object, try to extract message or convert to string
            return fieldError.message || fieldError.error || JSON.stringify(fieldError);
          }
          return String(fieldError);
        }
        return null;
      } catch {
        // If not JSON, treat as general error for email field (most common case)
        if (fieldName === 'email') {
          return auth.authError;
        }
        return null;
      }
    }
    
    return null;
  };

  // Reset touched states when a new error comes in
  React.useEffect(() => {
    if (auth.authError) {
      setEmailTouched(false);
    }
  }, [auth.authError]);

  // Custom onChange handler that tracks touched state
  const handleEmailChange = (value: string) => {
    setEmail(value);
    setEmailTouched(true);
  };

  const onSubmit = () => {
    dispatch(ResetPassword({ navigate, email }));
  };

  return (
    <>
      <div className="container">
        <Heading h2 className="mt-1">
          Email Confirmation
        </Heading>
        <Form onSubmit={onSubmit}>
          <Input
            type="text"
            autocomplete="username"
            required
            title="Please confirm your email"
            placeholder="Your email"
            state={{
              error: getFieldError('email') ? {
                condition: true,
                text: getFieldError('email'),
              } : {
                condition: email !== undefined && !validateEmail(email),
                text: "Invalid email format",
              },
              details: !getFieldError('email') && (email === undefined || validateEmail(email)) ? {
                condition: true,
                text: "Enter your email to reset password",
              } : null,
            }}
            value={email || ""}
            onChange={handleEmailChange}
          />
          <button
            disabled={!email || !validateEmail(email) || auth.loading}
            className={`${styles['btn']} ${styles['btn-primary']} mt-1`}
            onClick={onSubmit}
            type="submit"
          >
            Continue
          </button>
        </Form>
      </div>
    </>
  );
};
